import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { TimelineLanes } from "@/components/timeline-player/timeline-lanes"
import { PlaybackTransport, usePlaybackTransport } from "@/components/timeline-player/playback-transport"
import type { TimelineAudioAsset } from "@/components/shot-panel/timeline-editor/types"
import {
  MIN_CLIP_MS,
  SCHEMA_VERSION,
  useTimelinePlayback,
  type EngineSources,
  type TimelineDocument,
  type TimelineWaveform,
} from "@/lib/timeline"

/** Как у серверного WaveformPeaks: бакет 40 мс, амплитуда 0–255. */
const PEAK_INTERVAL_MS = 40
const TAKE_ASSET_ID = 1
const FRAME_MS = 1000 / 30

const EMPTY_SELECTED = new Map<string, readonly string[]>()
/** Подтягивать шот, только если он убежал больше чем на кадр-два. */
const DRIFT_CORRECT_MS = 120
/** Частые сики дёргают картинку: между подтяжками выдерживаем паузу. */
const DRIFT_COOLDOWN_MS = 400
const TAKE_KEY_TARGET =
  'input, textarea, select, [contenteditable="true"], [contenteditable=""], [contenteditable]:not([contenteditable="false"]), [role="listbox"], [role="menu"]'

function isTakeKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  if (target instanceof HTMLElement && target.isContentEditable) return true
  return Boolean(target.closest(TAKE_KEY_TARGET))
}

/** Шот, с которым дубль записан в синхроне. Время дубля — время шота: оба стартуют с 0. */
export type TakeVideoSync = {
  play: () => void
  pause: () => void
  seek: (ms: number) => void
  scrubTo: (ms: number) => void
  endScrub: () => void
  currentMs: () => number
  isPlaying: () => boolean
}

type Probe = {
  fileMs: number
  waveform: TimelineWaveform | null
}

function clipMs(fileMs: number, maxDurationMs: number | null): number {
  const capped = maxDurationMs != null ? Math.min(fileMs, maxDurationMs) : fileMs
  return Math.max(MIN_CLIP_MS, Math.round(capped))
}

/** Локальные пики для WaveformCanvas. null — декод не вышел, дорожка без волны. */
async function probeTake(file: Blob): Promise<Probe> {
  if (typeof AudioContext === "undefined") {
    return { fileMs: await durationFromElement(file), waveform: null }
  }
  const ctx = new AudioContext()
  try {
    const encoded = await file.arrayBuffer()
    const buffer = await ctx.decodeAudioData(encoded.slice(0))
    const channel = buffer.getChannelData(0)
    const samplesPerBucket = Math.max(1, Math.round((buffer.sampleRate * PEAK_INTERVAL_MS) / 1000))
    const peaks: number[] = []
    for (let i = 0; i < channel.length; i += samplesPerBucket) {
      let max = 0
      const end = Math.min(channel.length, i + samplesPerBucket)
      for (let j = i; j < end; j += 1) {
        const sample = Math.abs(channel[j] ?? 0)
        if (sample > max) max = sample
      }
      peaks.push(Math.round(Math.min(1, max) * 255))
    }
    return {
      fileMs: Math.round(buffer.duration * 1000),
      waveform: peaks.length > 0 ? { peaks, interval_ms: PEAK_INTERVAL_MS } : null,
    }
  } catch {
    return { fileMs: await durationFromElement(file), waveform: null }
  } finally {
    void ctx.close()
  }
}

function durationFromElement(file: Blob): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const audio = new Audio()
    const finish = (ms: number) => {
      URL.revokeObjectURL(url)
      resolve(ms)
    }
    audio.preload = "metadata"
    audio.onloadedmetadata = () => finish(Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : 0)
    audio.onerror = () => finish(0)
    audio.src = url
  })
}

type AudioTakeTimelineProps = {
  src: string
  file: Blob
  /** Сколько мс шот проиграл. null — показать файл целиком. */
  maxDurationMs: number | null
  /** null — дубль без видео, шот не трогаем. */
  videoSync?: TakeVideoSync | null
}

function takeLengthMs(durationMs: number): number {
  return durationMs > 0 ? durationMs : MIN_CLIP_MS
}

/** Прослушивание дубля: линейка и одна аудиодорожка. Видео, если было, играет снаружи. */
export function AudioTakeTimeline({ src, file, maxDurationMs, videoSync = null }: AudioTakeTimelineProps) {
  const [probe, setProbe] = useState<Probe | null>(null)
  const videoSyncRef = useRef(videoSync)
  videoSyncRef.current = videoSync

  useEffect(() => {
    let cancelled = false
    void probeTake(file).then((next) => {
      if (!cancelled) setProbe(next)
    })
    return () => {
      cancelled = true
    }
  }, [file])

  const durationMs = probe ? clipMs(probe.fileMs, maxDurationMs) : 0
  const doc = useMemo<TimelineDocument>(() => {
    const length = takeLengthMs(durationMs)
    return {
      schema_version: SCHEMA_VERSION,
      tracks: [
        {
          id: "take",
          kind: "custom_audio",
          muted: false,
          clips: [
            {
              id: "take-clip",
              audio_asset_id: TAKE_ASSET_ID,
              source_in_ms: 0,
              source_out_ms: length,
              timeline_start_ms: 0,
            },
          ],
        },
      ],
    }
  }, [durationMs])
  const sources = useMemo<EngineSources>(() => ({ versionUrls: {}, assetUrls: { [TAKE_ASSET_ID]: src } }), [src])
  const assets = useMemo<TimelineAudioAsset[]>(
    () => [
      {
        id: TAKE_ASSET_ID,
        kind: "recording",
        status: "ready",
        name: null,
        duration_ms: durationMs > 0 ? durationMs : null,
        error_message: null,
        url: src,
        waveform: probe?.waveform ?? null,
      },
    ],
    [durationMs, probe?.waveform, src],
  )

  const playback = useTimelinePlayback({
    doc,
    sources,
    onEnded: () => videoSyncRef.current?.pause(),
  })
  const playingRef = useRef(playback.playing)
  playingRef.current = playback.playing
  const { pauseAll, togglePlay, stepFrames, stepMs, goToStart, goToEnd } = usePlaybackTransport({
    controls: playback.controls,
    playing: playback.playing,
    frameMs: FRAME_MS,
    getEndMs: () => takeLengthMs(durationMs),
  })

  const followPaused = useCallback(() => {
    const sync = videoSyncRef.current
    if (!sync) return
    sync.pause()
    sync.seek(playback.controls.currentMs())
  }, [playback.controls])

  const pauseSynced = useCallback(() => {
    pauseAll()
    videoSyncRef.current?.pause()
  }, [pauseAll])

  const toggleSynced = useCallback(() => {
    const sync = videoSyncRef.current
    if (playback.controls.isPlaying()) {
      pauseSynced()
      return
    }
    const endMs = takeLengthMs(durationMs)
    const atEnd = playback.controls.currentMs() >= endMs - 1
    const ms = atEnd ? 0 : playback.controls.currentMs()
    if (atEnd) playback.controls.seek(0)
    sync?.seek(ms)
    sync?.play()
    togglePlay()
  }, [pauseSynced, togglePlay, durationMs, playback.controls])

  const stepFramesSynced = useCallback(
    (frames: number) => {
      stepFrames(frames)
      followPaused()
    },
    [stepFrames, followPaused],
  )

  const stepMsSynced = useCallback(
    (deltaMs: number) => {
      stepMs(deltaMs)
      followPaused()
    },
    [stepMs, followPaused],
  )

  const goToStartSynced = useCallback(() => {
    goToStart()
    followPaused()
  }, [goToStart, followPaused])

  const goToEndSynced = useCallback(() => {
    goToEnd()
    followPaused()
  }, [goToEnd, followPaused])

  useEffect(() => {
    return () => {
      videoSyncRef.current?.pause()
    }
  }, [])

  useEffect(() => {
    if (!videoSync || !playback.playing) return
    let lastCorrect = 0
    return playback.timeStore.subscribe(() => {
      const sync = videoSyncRef.current
      // isPlaying, не React-state: скраб и конец дубля глушат движок раньше рендера.
      if (!sync || !playback.controls.isPlaying()) return
      const now = performance.now()
      const takeMs = playback.timeStore.get()
      if (Math.abs(sync.currentMs() - takeMs) > DRIFT_CORRECT_MS) {
        if (now - lastCorrect < DRIFT_COOLDOWN_MS) return
        lastCorrect = now
        sync.seek(takeMs)
      }
      if (sync.isPlaying()) return
      // play() на самом конце шота сбрасывает плейхед в 0 — не перезапускаем его.
      if (Math.abs(sync.currentMs() - takeMs) > DRIFT_CORRECT_MS) return
      sync.play()
    })
  }, [videoSync, playback.playing, playback.timeStore, playback.controls])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || isTakeKeyTarget(event.target)) return
      switch (event.code) {
        case "Space":
          event.preventDefault()
          event.stopPropagation()
          if (event.repeat) return
          toggleSynced()
          return
        case "ArrowLeft":
          event.preventDefault()
          event.stopPropagation()
          if (event.shiftKey) stepMsSynced(-1000)
          else stepFramesSynced(-1)
          return
        case "ArrowRight":
          event.preventDefault()
          event.stopPropagation()
          if (event.shiftKey) stepMsSynced(1000)
          else stepFramesSynced(1)
          return
        case "Home":
          event.preventDefault()
          event.stopPropagation()
          goToStartSynced()
          return
        case "End":
          event.preventDefault()
          event.stopPropagation()
          goToEndSynced()
          return
        case "KeyK":
          event.preventDefault()
          event.stopPropagation()
          pauseSynced()
          return
        case "KeyJ":
        case "KeyL":
        case "KeyB":
        case "Delete":
        case "Backspace":
          // Шот под модалкой эти клавиши не получает: ускорение и реверс разъедутся с картинкой, резать тут нечего.
          event.preventDefault()
          event.stopPropagation()
          return
        default:
          return
      }
    }
    document.addEventListener("keydown", onKeyDown, true)
    return () => document.removeEventListener("keydown", onKeyDown, true)
  }, [toggleSynced, stepFramesSynced, stepMsSynced, goToStartSynced, goToEndSynced, pauseSynced])

  return (
    <div className="relative flex flex-col gap-2">
      {/* Движок кладёт сюда <audio>. display:none в части браузеров глушит звук. */}
      <div ref={playback.viewportRef} className="pointer-events-none absolute h-0 w-0 overflow-hidden" aria-hidden />
      <PlaybackTransport
        playing={playback.playing}
        onTogglePlay={toggleSynced}
        onStepFrames={stepFramesSynced}
        onGoToStart={goToStartSynced}
        onGoToEnd={goToEndSynced}
      />
      {durationMs > 0 ? (
        <TimelineLanes
          doc={doc}
          timelineSources={{}}
          assets={assets}
          durationMs={durationMs}
          showTrackHeaders={false}
          resetKey={src}
          timeStore={playback.timeStore}
          playingRef={playingRef}
          selectedIdsByTrack={EMPTY_SELECTED}
          onSeek={(ms) => {
            playback.controls.scrubTo(ms)
            videoSyncRef.current?.scrubTo(ms)
          }}
          onScrubStart={() => {
            playback.controls.pause()
            videoSyncRef.current?.pause()
          }}
          onScrubEnd={() => {
            playback.controls.endScrub()
            videoSyncRef.current?.endScrub()
          }}
          onSelectClip={() => {}}
          onMoveClip={() => {}}
          onResizeClip={() => {}}
          onDragEnd={() => {}}
        />
      ) : (
        <div className="h-12 rounded-md bg-ui-surface-sunken/60" />
      )}
    </div>
  )
}
